import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HiChevronDown, HiOutlineMenu, HiOutlineX } from 'react-icons/hi';
import { Link, useLocation } from 'react-router-dom';
import { services } from '../../lib/servicesData';

const Brand = () => (
  <Link to="/" className="group flex items-center gap-2.5" aria-label="Nexgeius home">
    <img src="/favicon.ico" alt="" width="36" height="36" className="h-9 w-9 object-contain" />
    <span className="text-[1.35rem] font-semibold tracking-[-0.04em] text-[#102A3A]">Nexgeius</span>
  </Link>
);

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    setMenuOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const openDropdown = () => {
    clearTimeout(closeTimer.current);
    setServicesOpen(true);
  };

  const closeDropdown = () => {
    closeTimer.current = setTimeout(() => setServicesOpen(false), 140);
  };

  const isServicesActive = location.pathname.startsWith('/services');
  const navLinks = [
    { label: 'Work', to: '/case-studies' },
    { label: 'Pricing', to: '/pricing' },
    { label: 'About', to: '/about' },
  ];

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-[#DCE4E8] bg-white/95 backdrop-blur-xl">
      <nav className="mx-auto flex h-[74px] max-w-7xl items-center justify-between px-6 lg:px-8" aria-label="Primary navigation">
        <Brand />

        <ul className="hidden items-center gap-1 lg:flex">
          <li onMouseEnter={openDropdown} onMouseLeave={closeDropdown}>
            <button
              type="button"
              onClick={() => setServicesOpen((value) => !value)}
              onFocus={openDropdown}
              aria-expanded={servicesOpen}
              className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition ${isServicesActive || servicesOpen ? 'bg-[#EAF2F5] text-[#176B8A]' : 'text-[#334D5B] hover:bg-[#F2F5F6] hover:text-[#176B8A]'}`}
            >
              Services
              <motion.span animate={{ rotate: servicesOpen ? 180 : 0 }} transition={{ duration: 0.18 }}><HiChevronDown size={14} /></motion.span>
            </button>
          </li>
          {navLinks.map((link) => (
            <li key={link.to}>
              <Link to={link.to} className={`rounded-lg px-4 py-2 text-sm font-medium transition ${location.pathname.startsWith(link.to) ? 'bg-[#EAF2F5] text-[#176B8A]' : 'text-[#334D5B] hover:bg-[#F2F5F6] hover:text-[#176B8A]'}`}>{link.label}</Link>
            </li>
          ))}
        </ul>

        <div className="hidden lg:block">
          <Link to="/contact" className="button-primary inline-flex items-center gap-2 !px-5 !py-2.5 !text-sm">Start a project <span aria-hidden="true">&rarr;</span></Link>
        </div>

        <button type="button" onClick={() => setMenuOpen((value) => !value)} className="rounded-lg p-2 text-[#102A3A] transition hover:bg-[#F2F5F6] lg:hidden" aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen}>
          {menuOpen ? <HiOutlineX size={24} /> : <HiOutlineMenu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {servicesOpen && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.16 }}
            className="absolute inset-x-0 top-full hidden border-b border-[#DCE4E8] bg-white shadow-[0_24px_60px_rgba(16,42,58,0.1)] lg:block"
            onMouseEnter={openDropdown}
            onMouseLeave={closeDropdown}
          >
            <div className="mx-auto max-w-7xl px-6 py-7 lg:px-8">
              <div className="mb-5 flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#82939C]">Choose an outcome</p>
                <Link to="/services" className="text-xs font-semibold text-[#176B8A]">Compare all services &rarr;</Link>
              </div>
              <div className="grid grid-cols-4 overflow-hidden rounded-2xl border border-[#DCE4E8]">
                {services.map((service) => (
                  <Link key={service.id} to={`/services/${service.id}`} className="group border-r border-[#DCE4E8] p-5 last:border-r-0 hover:bg-[#F6F8FA]">
                    <div className="service-icon flex h-9 w-9 items-center justify-center rounded-lg text-white" style={{ backgroundColor: service.accent }}>{service.icon}</div>
                    <p className="mt-5 text-sm font-semibold text-[#102A3A] group-hover:text-[#176B8A]">{service.title}</p>
                    <p className="mt-2 line-clamp-2 text-xs leading-5 text-[#6B7C85]">{service.shortDesc}</p>
                    <p className="mt-4 text-[11px] font-semibold text-[#176B8A]">From {service.startingAt}</p>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {menuOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden border-t border-[#DCE4E8] bg-white lg:hidden">
            <div className="max-h-[calc(100vh-74px)] overflow-y-auto px-6 py-5">
              <button type="button" onClick={() => setMobileServicesOpen((value) => !value)} className="flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold text-[#102A3A] hover:bg-[#F6F8FA]" aria-expanded={mobileServicesOpen}>
                Services <HiChevronDown className={`transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence initial={false}>
                {mobileServicesOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="ml-4 border-l border-[#C5D8E0] py-2 pl-3">
                      {services.map((service) => (
                        <Link key={service.id} to={`/services/${service.id}`} className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-[#334D5B] hover:bg-[#F6F8FA]">
                          <span className="service-icon flex h-7 w-7 items-center justify-center rounded-md text-white" style={{ backgroundColor: service.accent }}>{service.icon}</span>
                          {service.title}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              {navLinks.map((link) => (
                <Link key={link.to} to={link.to} className="block rounded-xl px-4 py-3 text-sm font-semibold text-[#102A3A] hover:bg-[#F6F8FA]">{link.label}</Link>
              ))}
              <Link to="/contact" className="button-primary mt-4 inline-flex w-full items-center justify-center !px-3 !py-3 !text-sm">Start a project</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
